import { useMemo } from 'react';
import { useTeamAllocations } from '../../hooks/usePeople';
import { addDays, fromDateKey, toDateKey } from '../../lib/calendarDates';
import { GanttChart, type GanttRow } from './GanttChart';
import { FadeIn, SkeletonTable } from '../ui/Skeleton';
import { useI18n } from '../../contexts/I18nContext';

interface GanttTeamProps {
  horizonStart: Date;
  weeks: number;
}

// Distinct colors cycled per project (same palette as CapacityChart).
const PALETTE = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-pink-500',
  'bg-cyan-500',
];

/**
 * Gantt of the team's allocations: one row per active person, one bar per
 * allocation labeled "{project} · {hours}h", colored per project.
 */
export function GanttTeam({ horizonStart, weeks }: GanttTeamProps) {
  const { t } = useI18n();
  const window = {
    from: toDateKey(horizonStart),
    to: toDateKey(addDays(horizonStart, weeks * 7 - 1)),
  };
  const { data: allocations, isLoading } = useTeamAllocations(window);

  const rows: GanttRow[] = useMemo(() => {
    const colorIndex = new Map<string, number>();
    const colorFor = (projectId: string) => {
      if (!colorIndex.has(projectId)) colorIndex.set(projectId, colorIndex.size);
      return PALETTE[(colorIndex.get(projectId) ?? 0) % PALETTE.length];
    };
    const byPerson = new Map<string, GanttRow>();
    for (const alloc of allocations ?? []) {
      let row = byPerson.get(alloc.person.id);
      if (!row) {
        row = {
          id: alloc.person.id,
          label: alloc.person.fullName,
          labelHref: `/people/${alloc.person.id}`,
          bars: [],
        };
        byPerson.set(alloc.person.id, row);
      }
      const label = `${alloc.project.name} · ${alloc.hours}h`;
      row.bars.push({
        id: alloc.id,
        start: fromDateKey(alloc.startDate),
        end: alloc.endDate ? fromDateKey(alloc.endDate) : null,
        color: colorFor(alloc.projectId),
        label,
        title: `${alloc.person.fullName}\n${label}`,
      });
    }
    return [...byPerson.values()];
  }, [allocations]);

  if (isLoading) {
    return <SkeletonTable rows={6} />;
  }

  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">{t('calendar.noTasks')}</p>;
  }

  return (
    <FadeIn>
      <GanttChart horizonStart={horizonStart} weeks={weeks} rows={rows} />
    </FadeIn>
  );
}
